import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Button, Grid, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { IconChevronRight } from '@tabler/icons';
import { Any } from 'currency.js';
import useAuth from 'hooks/useAuth';
import useClinicDocuments from 'hooks/useClinicDocuments';
import useUser from 'hooks/useUser';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import CenteredLoading from '../CenteredLoading';
import Documents from './Documents';
import DeleteDocumentModal from './modal/DeleteDocumentModal';
import EditAddCustomFormModal from './modal/EditAddCustomFormModal';
import EditAddDocumentModal from './modal/EditAddDocumentModal';

interface Props {
  setEditingFolderId?: Dispatch<SetStateAction<string | undefined>>;
  documentId?: Any;
  openNewFolderDialog?: () => void;
  selectOnly?: boolean;
  selectImageForUpload?: any;
  getDocument?: any;
  getCustomForm?: any;
  hideCustomForm?: boolean;
}

const ManageFolders = ({
  openNewFolderDialog,
  setEditingFolderId,
  selectOnly,
  selectImageForUpload,
  documentId,
  getDocument,
  getCustomForm,
  hideCustomForm = true
}: Props) => {
  const { documents, isDocumentsLoading } = useClinicDocuments();
  const intl = useIntl();
  const [creatingCustomForm, setCreatingCustomForm] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState();
  const { userId } = useAuth();
  const { isNousSupport } = useUser(userId);

  useEffect(() => {
    if (!selectedDocumentId && documents?.documents.length) {
      setSelectedDocumentId(documents?.documents[0]._id);
    }
  }, [documents, selectedDocumentId]);
  console.log('docs', documents);

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {!!openNewFolderDialog && (
          <Button variant="contained" onClick={openNewFolderDialog}>
            <FormattedMessage defaultMessage="New Folder" />
          </Button>
        )}

        <Stack direction="row" spacing={2}>
          {!!selectedDocumentId && !selectOnly && isNousSupport && (
            <Button variant="contained" onClick={() => setUploadingDocument(true)}>
              <FormattedMessage defaultMessage="Upload Documents" />
            </Button>
          )}

          {/* {!!selectedDocumentId && !selectOnly && (
            <Button variant="contained" onClick={() => setCreatingCustomForm(true)}>
              <FormattedMessage defaultMessage="Create Custom Form" />
            </Button>
          )} */}
        </Stack>
      </Stack>

      {creatingCustomForm && <EditAddCustomFormModal onClose={() => setCreatingCustomForm(false)} editingFolderId={selectedDocumentId} />}
      {uploadingDocument && <EditAddDocumentModal onClose={() => setUploadingDocument(false)} editingFolderId={selectedDocumentId} />}

      {isDocumentsLoading ? (
        <CenteredLoading />
      ) : (
        <Grid container mt={3}>
          <Grid item xs={3} sx={(theme) => ({ border: 1, borderColor: theme.palette.primary.light, borderRadius: 2, p: 1.5 })}>
            {documents?.documents.length > 0 ? (
              documents?.documents?.map(({ title, targetAudience, _id }, index) => (
                <Grid key={_id} onClick={() => setSelectedDocumentId(_id)}>
                  <Grid key={_id} item onClick={() => setSelectedDocumentId(_id)}>
                    <FolderItem
                      _id={_id}
                      title={title}
                      targetAudience={targetAudience}
                      setEditingFolderId={setEditingFolderId}
                      selectedDocumentId={selectedDocumentId}
                      isLastItem={index === documents.documents.length - 1}
                    />
                  </Grid>
                </Grid>
              ))
            ) : (
              <>
                <Typography textAlign={'center'} width={'100vw'} lineHeight={20}>
                  <FormattedMessage defaultMessage={'No folder to show'} />
                </Typography>
              </>
            )}
          </Grid>

          <Grid item xs={9} pl={2}>
            {!!documents?.documents.length && (
              <Documents
                documentId={selectedDocumentId}
                showDocumentActions={!selectOnly}
                selectImageForUpload={selectImageForUpload}
                getDocument={getDocument}
                getCustomForm={getCustomForm}
                hideCustomForm={hideCustomForm}
              />
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

const FolderItem = ({ _id, title, targetAudience, setEditingFolderId, selectedDocumentId, isLastItem }) => {
  const theme = useTheme();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [documentId, setSelectedDocumentId] = useState();
  const [folderId, setFolderId] = useState<string | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setOpenDeleteModal(false);
    setAnchorEl(null);
  };

  const handleDelete = (id: string) => {
    setFolderId(id);
    setOpenDeleteModal(true);
  };

  const isSelected = _id === selectedDocumentId;

  return (
    <>
      {!!openDeleteModal && <DeleteDocumentModal documentId={folderId} onClose={handleClose} />}

      <Stack
        direction="row"
        alignItems="center"
        py={1}
        pr={1}
        sx={(theme) => ({
          borderRadius: isSelected ? 2 : 0,
          backgroundColor: isSelected ? theme.palette.primary.light : '',
          borderBottom: isLastItem ? 0 : 0.1,
          borderColor: '#f2f9ff'
        })}
      >
        <IconButton onClick={handleClick}>
          <MoreVertIcon />
        </IconButton>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ cursor: 'pointer', flex: 1, justifyContent: 'space-between' }}
          onClick={() => setSelectedDocumentId(_id)}
        >
          <Typography sx={{ fontSize: 14 }}>{title}</Typography>
          <IconChevronRight size={20} />
        </Stack>

        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem
            onClick={() => {
              setEditingFolderId(_id);
              handleClose();
            }}
          >
            <FormattedMessage defaultMessage={'Edit Folder'} />
          </MenuItem>
          <MenuItem onClick={() => handleDelete(_id)}>
            <FormattedMessage defaultMessage={'Delete'} />
          </MenuItem>
        </Menu>
      </Stack>
    </>
  );
};

export default ManageFolders;
